import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash.get';
import { Grid, Typography, Card, Button, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import useStyles from '../styles';
import {
  buildFollowUpSummary,
  buildDefaultFollowUpName,
  getTodayDateInputValue,
  toDateInputValue,
  normalizeFindingType,
  findingTypeDisplayLabel,
} from '../helpers';
import FormInput from '../../../components/FormInput';
import FormSelect from '../../../components/FormSelect';
import FormDatePicker from '../../../components/FormDataPicker';
import PromptNotification from '../../../components/PromptComponent';
import LoadingButton from '../../../components/LoadingButton';
import ModalConfirmation from '../../../components/ModalConfirmation';
import { useCompanyInfo } from '../../../../utils/effects/useCompanyInfo';
import { useUserPermissions } from '../../../../utils/effects/userPermissions';
import { canEditPsmAsUser, normalizePersonId } from '../../../../utils/psmAuditPermissions';
import { psmAuditDigital } from '../../../../actionTypes';
import { userTypes } from '../../../../utils/constants';

const FOLLOW_UP_STATUS_BY_MODE = {
  saveProgress: 'InProgress',
  complete: 'Completed',
};

const FollowUpForm = ({ id, auditId, onBack }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const submitModeRef = useRef('saveProgress');
  const createdFollowUpIdRef = useRef(null);
  const pendingFormResetRef = useRef(null);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const currentUserId = useSelector(state => state.auth.check.userId);
  const currentUser = useSelector(state => get(state, `entity.user[${currentUserId}]`, {}));
  const followUpState = useSelector(state => state.psmAuditDigital);
  const followUp = followUpState.followUp;
  const { companyName } = useCompanyInfo(id);
  const { type: userType } = useUserPermissions();

  useEffect(() => {
    createdFollowUpIdRef.current = null;
    if (auditId && auditId !== 'new') {
      dispatch({ type: psmAuditDigital.followUpGet.start, payload: { auditId } });
    }
  }, [auditId, dispatch]);

  useEffect(() => {
    const followUpId = get(followUp, 'id', null);
    if (followUpId) {
      createdFollowUpIdRef.current = followUpId;
    }
  }, [followUp]);

  useEffect(() => {
    if (!followUpState.followUpSaveSuccess || !pendingFormResetRef.current) return;
    const { values, resetForm } = pendingFormResetRef.current;
    const followUpId = get(followUp, 'id', null) ?? createdFollowUpIdRef.current;
    if (followUpId) {
      createdFollowUpIdRef.current = followUpId;
    }
    resetForm({ values: { ...values, followUpId: followUpId ?? values.followUpId } });
    pendingFormResetRef.current = null;
  }, [followUpState.followUpSaveSuccess, followUp]);

  useEffect(() => {
    if (followUpState.followUpSaveError) {
      pendingFormResetRef.current = null;
    }
  }, [followUpState.followUpSaveError]);

  const mapItems = useCallback(
    items =>
      (items || []).map((item, index) => {
        const status = item.status || 'StillPending';
        return {
          localKey: `${item.id || 0}-${index}`,
          id: item.id,
          type: normalizeFindingType(item.findingType),
          issuedDate: toDateInputValue(item.issuedDate),
          targetDate: toDateInputValue(item.targetDate),
          shortDescription: item.shortDescription,
          detailedDescription: item.detailedDescription,
          originalComment: item.originalComment,
          status,
          completionNotes: item.completionNotes || '',
          completionDate:
            status === 'StillPending'
              ? toDateInputValue(item.completionDate) || null
              : toDateInputValue(item.completionDate) || getTodayDateInputValue(),
        };
      }),
    [],
  );

  const displayContractor = get(followUp, 'contractor.name', '') || companyName || '';
  const displayOriginalAuditor = get(followUp, 'originalAuditor.name', '');
  const displayAuditDate = toDateInputValue(get(followUp, 'auditDate', ''));
  const currentUserName =
    `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || '';
  const adminOptions = [{ label: currentUserName, value: currentUserName }];

  const initialValues = useMemo(() => {
    const today = getTodayDateInputValue();
    const followUpDate = toDateInputValue(get(followUp, 'followUpDate', '')) || today;
    const followUpEntries = followUp && Array.isArray(followUp.items) ? mapItems(followUp.items) : [];

    const isNewFollowUp = !get(followUp, 'id', null);

    return {
      followUpId: get(followUp, 'id', null),
      auditName: isNewFollowUp
        ? buildDefaultFollowUpName(displayContractor)
        : get(followUp, 'name', '') || buildDefaultFollowUpName(displayContractor),
      contractorName: displayContractor,
      originalAuditorName: displayOriginalAuditor,
      auditDate: displayAuditDate,
      followUpDate,
      followUpBy: currentUserName,
      followUpEntries,
      followUpSummary:
        get(followUp, 'summary', '') ||
        buildFollowUpSummary({
          followUpDate,
          correctedCount: 0,
          pendingCount: followUpEntries.length,
        }),
      status: get(followUp, 'status', 'InProgress'),
    };
  }, [followUp, currentUserName, mapItems, displayContractor, displayOriginalAuditor, displayAuditDate]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => {
        if (userType !== userTypes.SafetyCouncil) {
          return;
        }

        const mode = submitModeRef.current;
        const newStatus = FOLLOW_UP_STATUS_BY_MODE[mode] || 'InProgress';
        const persistedFollowUpId = values.followUpId || createdFollowUpIdRef.current || get(followUp, 'id', null);
        const valuesWithStatus = {
          ...values,
          status: newStatus,
          followUpId: persistedFollowUpId ?? values.followUpId,
        };
        pendingFormResetRef.current = { values: valuesWithStatus, resetForm };

        const payload = {
          followUpById: Number(currentUserId || 0),
          name: values.auditName,
          followUpDate: values.followUpDate,
          summary: values.followUpSummary,
          findings: values.followUpEntries.map(item => {
            const status = item.status || 'StillPending';
            const base = {
              id: item.id || 0,
              status,
              completionNotes: item.completionNotes || '',
            };

            // Backend should not receive completion date changes for still-pending findings.
            if (status === 'StillPending') return base;

            return { ...base, completionDate: item.completionDate || null };
          }),
          status: newStatus,
        };

        const companyId = Number(id || 0);

        if (persistedFollowUpId) {
          dispatch({
            type: psmAuditDigital.followUpUpdate.start,
            payload: {
              auditFollowUpId: persistedFollowUpId,
              auditId,
              data: payload,
              companyId,
            },
          });
          return;
        }

        dispatch({
          type: psmAuditDigital.followUpCreate.start,
          payload: { auditId, data: payload, companyId },
        });
      }}
      enableReinitialize
    >
      {props => {
        const followUpEntries = props.values.followUpEntries;
        const correctedCount = followUpEntries.filter(item => item.status === 'Corrected').length;
        const pendingCount = followUpEntries.filter(item => item.status === 'StillPending').length;
        const savedStatus = get(followUp, 'status', 'InProgress');
        const isCompleted = savedStatus === 'Completed';
        const persistedFollowUpId =
          props.values.followUpId || createdFollowUpIdRef.current || get(followUp, 'id', null);
        const creatorId = normalizePersonId(followUp?.followUpBy) ?? normalizePersonId(followUp?.followUpById);
        const canEditFollowUp = !persistedFollowUpId
          ? true
          : followUp?.editable != null
          ? followUp.editable
          : canEditPsmAsUser(creatorId ?? Number(currentUserId), currentUserId, userType);
        const isFormLocked = isCompleted || (persistedFollowUpId && !canEditFollowUp);
        const showCompleteFollowUp = savedStatus !== 'Completed';

        const regenerateFollowUpSummary = () => {
          props.setFieldValue(
            'followUpSummary',
            buildFollowUpSummary({
              followUpDate: props.values.followUpDate,
              correctedCount,
              pendingCount,
            }),
          );
        };

        const handleFollowUpChange = (entryIndex, field, value) => {
          props.setFieldValue(
            'followUpEntries',
            followUpEntries.map((item, idx) => {
              if (idx !== entryIndex) return item;
              const updated = { ...item, [field]: value };
              if (field === 'status') {
                if (value === 'Corrected' && !updated.completionDate) {
                  updated.completionDate = getTodayDateInputValue();
                }
                if (value === 'StillPending') {
                  updated.completionDate = null;
                }
              }
              return updated;
            }),
          );
        };

        const handleConfirmComplete = () => {
          setCompleteConfirmOpen(false);
          submitModeRef.current = 'complete';
          props.handleSubmit();
        };

        return (
          <>
            <ModalConfirmation
              isModalOpen={completeConfirmOpen}
              title="Mark Follow-Up as Completed?"
              confirmButtonText="Confirm"
              cancelButtonText="Cancel"
              onCancel={() => setCompleteConfirmOpen(false)}
              onConfirm={handleConfirmComplete}
            >
              <Typography variant="body2">
                Once completed, the Follow-Up will become read-only and no further changes can be made.
              </Typography>
            </ModalConfirmation>
            <PromptNotification formStatus={!isFormLocked && props.dirty && !followUpState.pendingCompanyRedirect} />
            <Grid container className={classes.root} spacing={3}>
              <Grid item xs={12}>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack} className={classes.backButton}>
                  Back to Audits
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <Typography variant="h5" className={classes.pageTitle}>
                    Audit Follow-Up
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Company ID: {id} · Audit ID: {auditId}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Original Audit
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Field component={FormInput} label="Follow-Up Name" name="auditName" disabled={isFormLocked} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Field component={FormInput} label="Company" name="contractorName" disabled />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Field component={FormInput} label="Original Auditor" name="originalAuditorName" disabled />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Field component={FormDatePicker} label="Audit Date" name="auditDate" disabled />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Field
                        component={FormSelect}
                        label="Follow-Up By"
                        name="followUpBy"
                        options={adminOptions}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Field
                        component={FormDatePicker}
                        label="Follow-Up Date"
                        name="followUpDate"
                        disabled={isFormLocked}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Findings from Original Audit
                  </Typography>

                  {followUpEntries.map((item, index) => (
                    <Card key={item.localKey || item.id || index} variant="outlined" className={classes.followUpCard}>
                      <Typography variant="subtitle1" className={classes.followUpCardTitle}>
                        Finding #{index + 1}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" className={classes.followUpFieldLabel}>
                            Type
                          </Typography>
                          <Typography variant="body1">{findingTypeDisplayLabel(item.type)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            component={FormDatePicker}
                            label="Issued Date"
                            name={`followUpEntries.${index}.issuedDate`}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            component={FormDatePicker}
                            label="Target Date"
                            name={`followUpEntries.${index}.targetDate`}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            component={FormInput}
                            label="Description"
                            name={`followUpEntries.${index}.shortDescription`}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            component={FormInput}
                            label="Comment"
                            multiline
                            rows={2}
                            name={`followUpEntries.${index}.originalComment`}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" className={classes.followUpFieldLabel}>
                            Correction Status
                          </Typography>
                          <RadioGroup
                            row
                            value={item.status}
                            onChange={
                              isFormLocked
                                ? undefined
                                : event => handleFollowUpChange(index, 'status', event.target.value)
                            }
                          >
                            <FormControlLabel
                              value="Corrected"
                              control={<Radio color="primary" disabled={isFormLocked} />}
                              label="Corrected"
                              disabled={isFormLocked}
                            />
                            <FormControlLabel
                              value="StillPending"
                              control={<Radio color="primary" disabled={isFormLocked} />}
                              label="Still Pending"
                              disabled={isFormLocked}
                            />
                          </RadioGroup>
                        </Grid>
                        {item.status !== 'StillPending' && (
                          <Grid item xs={12} md={4}>
                            <Field
                              component={FormDatePicker}
                              label="Completion Date"
                              name={`followUpEntries.${index}.completionDate`}
                              disabled={isFormLocked}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Field
                            component={FormInput}
                            label="Completion Notes"
                            multiline
                            rows={3}
                            name={`followUpEntries.${index}.completionNotes`}
                            disabled={isFormLocked}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <div className={classes.summaryHeader}>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      Follow-Up Summary
                    </Typography>
                    {!isFormLocked && (
                      <Button variant="outlined" color="primary" onClick={regenerateFollowUpSummary}>
                        Regenerate Summary
                      </Button>
                    )}
                  </div>
                  <Field component={FormInput} multiline rows={5} name="followUpSummary" disabled={isFormLocked} />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.footerCard}>
                  <div className={classes.progressRow}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Typography variant="overline" className={classes.totalLabel}>
                          Corrected
                        </Typography>
                        <Typography variant="h5" className={classes.totalBestPracticeValue}>
                          {correctedCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="overline" className={classes.totalLabel}>
                          Still Pending
                        </Typography>
                        <Typography variant="h5" className={classes.totalRecommendationValue}>
                          {pendingCount}
                        </Typography>
                      </Grid>
                    </Grid>
                  </div>
                  <div className={classes.footerActions}>
                    <Button onClick={onBack}>{isFormLocked && !isCompleted ? 'Back' : 'Cancel'}</Button>
                    {!isFormLocked && (
                      <>
                        <Button
                          variant="outlined"
                          color="primary"
                          disabled={followUpState.followUpSaving}
                          onClick={() => {
                            submitModeRef.current = 'saveProgress';
                            props.submitForm();
                          }}
                        >
                          Save Progress
                        </Button>
                        {showCompleteFollowUp && (
                          <div className={classes.loadingButtonInline}>
                            <LoadingButton
                              skipCheckPQF
                              disabled={false}
                              loading={followUpState.followUpSaving}
                              success={followUpState.followUpSaveSuccess}
                              error={followUpState.followUpSaveError}
                              onClick={() => setCompleteConfirmOpen(true)}
                              text="Mark as Completed"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Card>
              </Grid>
            </Grid>
          </>
        );
      }}
    </Formik>
  );
};

export default FollowUpForm;
